import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  Loader2, LogOut, Download, Truck, Package, Tag,
  Mail, MessageSquare, DollarSign, ShoppingBag, TrendingUp,
  ExternalLink, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import {
  fetchAdminMe, fetchAdminStats, fetchAdminRevenue, fetchAdminOrders,
  fetchAdminTopProducts, fetchAdminCoupons, fetchAdminNewsletter,
  fetchAdminContacts, adminLogout, buyShippingLabel,
  adminOrdersExportUrl, adminNewsletterExportUrl,
} from "../../lib/api";

const TABS = [
  { key: "overview", label: "Overview", Icon: TrendingUp },
  { key: "orders", label: "Orders", Icon: ShoppingBag },
  { key: "products", label: "Products", Icon: Package },
  { key: "coupons", label: "Coupons", Icon: Tag },
  { key: "newsletter", label: "Newsletter", Icon: Mail },
  { key: "contacts", label: "Contacts", Icon: MessageSquare },
];

const fmtMoney = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

export default function AdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [authChecked, setAuthChecked] = useState(!!location.state?.user);
  const [tab, setTab] = useState("overview");

  // Auth gate
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return; // AuthCallback handles
    if (user) return;
    (async () => {
      try {
        const me = await fetchAdminMe();
        setUser(me);
      } catch {
        navigate("/admin/login", { replace: true });
      } finally {
        setAuthChecked(true);
      }
    })();
  }, [user, navigate]);

  const handleLogout = async () => {
    try { await adminLogout(); } catch {}
    setUser(null);
    navigate("/admin/login", { replace: true });
  };

  if (!user || !authChecked) {
    return (
      <div className="bg-[#050505] min-h-screen flex items-center justify-center text-white">
        <Loader2 className="h-6 w-6 animate-spin text-[#D10000]" />
      </div>
    );
  }

  return (
    <div className="bg-[#050505] min-h-screen text-white" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="border-b border-white/10 sticky top-0 z-30 backdrop-blur-xl bg-black/80">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="font-display text-xs tracking-[0.3em] text-[#D10000]">
              PRIMENAX
            </div>
            <span className="text-white/30">·</span>
            <div className="font-display tracking-widest uppercase text-sm">Control</div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-view-site-link" className="text-xs text-white/50 hover:text-white tracking-widest uppercase">
              View Site ↗
            </Link>
            <div className="hidden md:flex items-center gap-2 text-xs text-white/55">
              {user.picture && (
                <img src={user.picture} alt="" className="h-6 w-6 rounded-full" referrerPolicy="no-referrer" />
              )}
              <span className="truncate max-w-[180px]">{user.email}</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#D10000] px-3 py-2 text-xs tracking-widest uppercase transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </button>
          </div>
        </div>
        <nav className="max-w-[1500px] mx-auto px-6 md:px-10 flex items-center gap-1 overflow-x-auto scrollbar-none border-t border-white/5">
          {TABS.map(({ key, label, Icon }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                onClick={() => setTab(key)}
                data-testid={`admin-tab-${key}`}
                className={`inline-flex items-center gap-2 py-3 px-4 text-sm font-display tracking-widest uppercase border-b-2 transition-colors whitespace-nowrap ${
                  active
                    ? "border-[#D10000] text-white"
                    : "border-transparent text-white/45 hover:text-white"
                }`}
              >
                <Icon className="h-4 w-4" /> {label}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="max-w-[1500px] mx-auto px-6 md:px-10 py-10">
        {tab === "overview" && <OverviewTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "products" && <ProductsTab />}
        {tab === "coupons" && <CouponsTab />}
        {tab === "newsletter" && <NewsletterTab />}
        {tab === "contacts" && <ContactsTab />}
      </main>
    </div>
  );
}

// ---------- Overview ----------
const KPI = ({ label, value, sub, Icon }) => (
  <div className="bg-[#0A0A0A] border border-white/10 p-6" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start justify-between mb-3">
      <div className="font-display text-[10px] tracking-[0.3em] text-[#D10000]">
        {label.toUpperCase()}
      </div>
      <Icon className="h-4 w-4 text-white/30" />
    </div>
    <div className="font-display text-3xl md:text-4xl tracking-tight">{value}</div>
    {sub && <div className="text-xs text-white/45 mt-2">{sub}</div>}
  </div>
);

const OverviewTab = () => {
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState({ series: [] });
  const [topProducts, setTopProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, r, p] = await Promise.all([
        fetchAdminStats(),
        fetchAdminRevenue({ range: "daily", days: 30 }),
        fetchAdminTopProducts(),
      ]);
      setStats(s);
      setRevenue(r);
      setTopProducts(p.items || []);
    } catch (e) {
      toast.error("Failed to load stats");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const maxRev = useMemo(
    () => Math.max(1, ...(revenue.series || []).map((b) => b.revenue)),
    [revenue.series]
  );

  if (loading && !stats) return <Center><Loader2 className="h-5 w-5 animate-spin text-[#D10000]" /></Center>;
  if (!stats) return null;

  return (
    <div className="space-y-10">
      <div className="flex items-center justify-between">
        <h1 className="font-display tracking-tight uppercase text-3xl md:text-4xl">Overview</h1>
        <button onClick={refresh} data-testid="overview-refresh-button" className="inline-flex items-center gap-2 text-xs text-white/55 hover:text-white tracking-widest uppercase">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
        <KPI label="Total Revenue" value={fmtMoney(stats.total_revenue)} sub={`${stats.total_orders} orders all-time`} Icon={DollarSign} />
        <KPI label="Last 7 Days" value={fmtMoney(stats.last_7d_revenue)} sub={`${stats.last_7d_orders} new orders`} Icon={TrendingUp} />
        <KPI label="AOV" value={fmtMoney(stats.average_order_value)} sub="avg order value" Icon={ShoppingBag} />
        <KPI label="Pending Labels" value={stats.pending_fulfillment} sub="orders awaiting Shippo label" Icon={Truck} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue bar chart */}
        <div className="lg:col-span-2 bg-[#0A0A0A] border border-white/10 p-6 md:p-8" data-testid="revenue-chart">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="font-display text-[10px] tracking-[0.3em] text-[#D10000]">REVENUE · LAST 30 DAYS</div>
              <div className="font-display text-2xl tracking-tight mt-1">{fmtMoney(revenue.series.reduce((s,b)=>s+b.revenue,0))}</div>
            </div>
            <div className="text-xs text-white/45">{revenue.series.length} active days</div>
          </div>
          {revenue.series.length === 0 ? (
            <div className="text-white/40 py-10 text-center">No orders in the last 30 days yet.</div>
          ) : (
            <div className="flex items-end gap-1 h-48">
              {revenue.series.map((b) => (
                <div key={b.bucket} className="flex-1 group relative" title={`${b.bucket} · ${fmtMoney(b.revenue)} · ${b.orders} orders`}>
                  <div
                    className="w-full bg-[#D10000]/40 hover:bg-[#D10000] transition-colors"
                    style={{ height: `${Math.max(2, (b.revenue / maxRev) * 100)}%` }}
                  />
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-between mt-2 text-[10px] text-white/35">
            {revenue.series[0] && <span>{revenue.series[0].bucket}</span>}
            {revenue.series.length > 1 && <span>{revenue.series[revenue.series.length - 1].bucket}</span>}
          </div>
        </div>

        {/* Top products */}
        <div className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8" data-testid="top-products-card">
          <div className="font-display text-[10px] tracking-[0.3em] text-[#D10000] mb-4">TOP PRODUCTS</div>
          {topProducts.length === 0 ? (
            <div className="text-white/40 text-sm">No sales yet.</div>
          ) : (
            <ul className="space-y-3">
              {topProducts.slice(0, 6).map((p, i) => (
                <li key={p.product_id} className="flex items-center justify-between gap-3 border-b border-white/5 pb-3 last:border-b-0 last:pb-0">
                  <div className="min-w-0">
                    <div className="font-display text-xs tracking-widest text-white/40">#{String(i+1).padStart(2,'0')}</div>
                    <div className="font-display tracking-tight uppercase text-sm truncate">{p.name}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-lg">{p.units}</div>
                    <div className="text-[10px] text-white/40 tracking-widest uppercase">units</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------- Orders ----------
const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchAdminOrders({ q: q || undefined, limit: 50 });
      setOrders(res.items || []);
      setTotal(res.total || 0);
    } catch {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // initial
  const search = (e) => { e.preventDefault(); load(); };

  const buyLabel = async (id) => {
    setPurchasing((p) => ({ ...p, [id]: true }));
    try {
      const res = await buyShippingLabel(id);
      toast.success(`Label purchased · tracking ${res.tracking_number || "—"}`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Label purchase failed");
    } finally {
      setPurchasing((p) => ({ ...p, [id]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <h1 className="font-display tracking-tight uppercase text-3xl md:text-4xl">Orders <span className="text-white/30 text-base">({total})</span></h1>
        <div className="flex items-center gap-3">
          <form onSubmit={search} className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Search email / order id / tracking"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="orders-search-input"
              className="bg-transparent border border-white/15 focus:border-[#D10000] outline-none px-3 py-2 text-sm w-72 text-white placeholder:text-white/30"
            />
            <button type="submit" data-testid="orders-search-button" className="text-xs tracking-widest uppercase border border-white/15 hover:border-[#D10000] px-4 py-2">Search</button>
          </form>
          <a
            href={adminOrdersExportUrl}
            data-testid="orders-export-csv-link"
            className="inline-flex items-center gap-2 bg-[#D10000] hover:bg-[#A30000] text-white text-xs tracking-widest uppercase px-4 py-2"
          >
            <Download className="h-3.5 w-3.5" /> CSV
          </a>
        </div>
      </div>

      <div className="bg-[#0A0A0A] border border-white/10 overflow-x-auto">
        {loading ? (
          <div className="p-10"><Loader2 className="h-5 w-5 animate-spin text-[#D10000] mx-auto" /></div>
        ) : orders.length === 0 ? (
          <div className="p-10 text-white/40 text-center">No orders yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="orders-table">
            <thead className="border-b border-white/10 text-[10px] tracking-[0.25em] text-white/40 uppercase">
              <tr>
                <th className="text-left p-4">Date</th>
                <th className="text-left p-4">Customer</th>
                <th className="text-left p-4">Items</th>
                <th className="text-right p-4">Total</th>
                <th className="text-left p-4">Tracking</th>
                <th className="text-right p-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const items = o.items || [];
                const addr = o.shipping_address || {};
                const hasTrack = !!o.tracking_number;
                return (
                  <tr key={o.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`order-row-${o.id}`}>
                    <td className="p-4 align-top">
                      <div className="text-white/85">{(o.created_at || "").slice(0,10)}</div>
                      <div className="text-[10px] text-white/35">{o.id?.slice(0,8)}…</div>
                    </td>
                    <td className="p-4 align-top">
                      <div className="text-white/85">{o.customer_email || "—"}</div>
                      <div className="text-[11px] text-white/40">{addr.city || ""}{addr.city && addr.state ? ", " : ""}{addr.state || ""}</div>
                    </td>
                    <td className="p-4 align-top text-white/70 text-xs">
                      {items.map((i) => `${i.product_id} ×${i.quantity}`).join(", ")}
                    </td>
                    <td className="p-4 align-top text-right">
                      <div className="text-white/90 font-display tracking-tight">{fmtMoney(o.amount)}</div>
                      {o.promotion_code && <div className="text-[10px] text-[#D10000] tracking-widest">{o.promotion_code}</div>}
                    </td>
                    <td className="p-4 align-top">
                      {hasTrack ? (
                        <a
                          href={o.tracking_url || `https://tools.usps.com/go/TrackConfirmAction?tLabels=${o.tracking_number}`}
                          target="_blank" rel="noreferrer"
                          className="text-[#D10000] hover:underline inline-flex items-center gap-1 text-xs"
                          data-testid={`tracking-link-${o.id}`}
                        >
                          {o.tracking_number} <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-white/35 text-xs">Pending</span>
                      )}
                    </td>
                    <td className="p-4 align-top text-right">
                      {!hasTrack && (
                        <button
                          onClick={() => buyLabel(o.id)}
                          disabled={purchasing[o.id]}
                          data-testid={`buy-label-button-${o.id}`}
                          className="inline-flex items-center gap-2 text-xs tracking-widest uppercase border border-white/15 hover:border-[#D10000] disabled:opacity-40 px-3 py-2"
                        >
                          {purchasing[o.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Truck className="h-3 w-3" />}
                          Buy Label
                        </button>
                      )}
                      {hasTrack && o.label_url && (
                        <a href={o.label_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-white/55 hover:text-white">
                          Label <Download className="h-3 w-3" />
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ---------- Products ----------
const ProductsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetchAdminTopProducts();
        setItems(r.items || []);
      } finally { setLoading(false); }
    })();
  }, []);
  return (
    <div className="space-y-6">
      <h1 className="font-display tracking-tight uppercase text-3xl md:text-4xl">Top Products</h1>
      <div className="bg-[#0A0A0A] border border-white/10">
        {loading ? <div className="p-10"><Loader2 className="h-5 w-5 animate-spin text-[#D10000] mx-auto" /></div>
        : items.length === 0 ? <div className="p-10 text-white/40 text-center">No sales data yet.</div>
        : (
          <table className="w-full text-sm" data-testid="top-products-table">
            <thead className="border-b border-white/10 text-[10px] tracking-[0.25em] text-white/40 uppercase">
              <tr>
                <th className="text-left p-4">Rank</th>
                <th className="text-left p-4">Product</th>
                <th className="text-right p-4">Units Sold</th>
                <th className="text-right p-4">Orders</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p, i) => (
                <tr key={p.product_id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4 font-display text-white/40">#{String(i+1).padStart(2,'0')}</td>
                  <td className="p-4 font-display tracking-tight uppercase">{p.name}</td>
                  <td className="p-4 text-right font-display text-lg">{p.units}</td>
                  <td className="p-4 text-right text-white/55">{p.orders}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ---------- Coupons ----------
const CouponsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetchAdminCoupons();
        setItems(r.items || []);
      } finally { setLoading(false); }
    })();
  }, []);
  return (
    <div className="space-y-6">
      <h1 className="font-display tracking-tight uppercase text-3xl md:text-4xl">Coupons</h1>
      <div className="bg-[#0A0A0A] border border-white/10">
        {loading ? <div className="p-10"><Loader2 className="h-5 w-5 animate-spin text-[#D10000] mx-auto" /></div>
        : items.length === 0 ? <div className="p-10 text-white/40 text-center">No coupon redemptions yet.</div>
        : (
          <table className="w-full text-sm" data-testid="coupons-table">
            <thead className="border-b border-white/10 text-[10px] tracking-[0.25em] text-white/40 uppercase">
              <tr>
                <th className="text-left p-4">Code</th>
                <th className="text-right p-4">Redemptions</th>
                <th className="text-right p-4">Total Discount</th>
                <th className="text-right p-4">Driven Revenue</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.code} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4 font-display tracking-widest text-[#D10000]">{c.code}</td>
                  <td className="p-4 text-right font-display text-lg">{c.redemptions}</td>
                  <td className="p-4 text-right text-white/85">{fmtMoney(c.total_discount)}</td>
                  <td className="p-4 text-right text-white/85">{fmtMoney(c.total_revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ---------- Newsletter ----------
const NewsletterTab = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetchAdminNewsletter({ limit: 200 });
        setItems(r.items || []);
        setTotal(r.total || 0);
      } finally { setLoading(false); }
    })();
  }, []);
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <h1 className="font-display tracking-tight uppercase text-3xl md:text-4xl">Newsletter <span className="text-white/30 text-base">({total})</span></h1>
        <a href={adminNewsletterExportUrl} data-testid="newsletter-export-csv-link" className="inline-flex items-center gap-2 bg-[#D10000] hover:bg-[#A30000] text-xs tracking-widest uppercase px-4 py-2">
          <Download className="h-3.5 w-3.5" /> CSV
        </a>
      </div>
      <div className="bg-[#0A0A0A] border border-white/10">
        {loading ? <div className="p-10"><Loader2 className="h-5 w-5 animate-spin text-[#D10000] mx-auto" /></div>
        : items.length === 0 ? <div className="p-10 text-white/40 text-center">No subscribers yet.</div>
        : (
          <table className="w-full text-sm" data-testid="newsletter-table">
            <thead className="border-b border-white/10 text-[10px] tracking-[0.25em] text-white/40 uppercase">
              <tr>
                <th className="text-left p-4">Email</th>
                <th className="text-left p-4">Source</th>
                <th className="text-right p-4">Joined</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4 text-white/85">{s.email}</td>
                  <td className="p-4 text-white/40 text-xs">{s.source || "—"}</td>
                  <td className="p-4 text-right text-white/45 text-xs">{(s.created_at || "").slice(0,10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ---------- Contacts ----------
const ContactsTab = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetchAdminContacts({ limit: 100 });
        setItems(r.items || []);
        setTotal(r.total || 0);
      } finally { setLoading(false); }
    })();
  }, []);
  return (
    <div className="space-y-6">
      <h1 className="font-display tracking-tight uppercase text-3xl md:text-4xl">Contact Messages <span className="text-white/30 text-base">({total})</span></h1>
      <div className="space-y-3">
        {loading ? <div className="p-10"><Loader2 className="h-5 w-5 animate-spin text-[#D10000] mx-auto" /></div>
        : items.length === 0 ? <div className="bg-[#0A0A0A] border border-white/10 p-10 text-white/40 text-center">No contact messages yet.</div>
        : items.map((c, i) => (
          <div key={i} className="bg-[#0A0A0A] border border-white/10 p-5 md:p-6" data-testid={`contact-row-${i}`}>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <div className="font-display tracking-tight uppercase">{c.name}</div>
                <a href={`mailto:${c.email}`} className="text-[#D10000] hover:underline text-xs">{c.email}</a>
              </div>
              <div className="text-[10px] text-white/40 tracking-widest uppercase">{(c.created_at||"").slice(0,16).replace("T"," ")}</div>
            </div>
            {c.subject && <div className="font-display text-sm tracking-wider text-white/65 mb-2">{c.subject}</div>}
            <p className="text-white/80 text-sm whitespace-pre-wrap leading-relaxed">{c.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const Center = ({ children }) => (
  <div className="flex items-center justify-center min-h-[300px]">{children}</div>
);
